import React, { Fragment, useState, useEffect } from "react"; 
import PortalMenu from "../Common/PortalMenu";
import { usePersistentState } from "../Common/usePersistentFilters";
 import PageTitle from "../../layouts/PageTitle"; 
 import { Row, Col, Card, Table, Dropdown, Modal, Form, Badge } from "react-bootstrap"; 
import AppPagination from "../Common/AppPagination"; 
 import swal from "sweetalert"; 
 import api from "../../../api"; 
 import usePermission from "../../../hooks/usePermission"; 
 
 const Roles = () => { 
 const { hasPermission } = usePermission(); 
 const [roles, setRoles] = useState([]); 
 const [permissions, setPermissions] = useState([]); 
 const [searchQuery, setSearchQuery] = useState(""); 
 const [roleModal, setRoleModal] = useState(false); 
 const [editRoleModal, setEditRoleModal] = useState(false); 
 const [newRole, setNewRole] = useState({ name: "", description: "", permissions: [] }); 
 const [cloneRoleId, setCloneRoleId] = useState(""); 
 const [editRole, setEditRole] = useState(null); 
 const [permSearch, setPermSearch] = useState("");
 
 // Pagination state
 const [currentPage, setCurrentPage] = usePersistentState("roles_page", 1);
 const [itemsPerPage] = useState(10);
 
 const canCreateRole = hasPermission("create:roles"); 
 const canEditRole = hasPermission("edit:roles"); 
 const canDeleteRole = hasPermission("delete:roles"); 
 
 useEffect(() => { 
 fetchRoles(); 
 fetchPermissions(); 
 }, []); 
 
 const fetchRoles = () => { 
 api.get('/roles?limit=999') 
 .then(res => setRoles(res.data.data || [])) 
 .catch(() => setRoles([])); 
 }; 
 
 const fetchPermissions = () => { 
 api.get('/permissions?limit=999') 
 .then(res => setPermissions(res.data.data || [])) 
 .catch(() => setPermissions([])); 
 }; 
 
 const filteredRoles = roles.filter(role => 
 role.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
 role.description?.toLowerCase().includes(searchQuery.toLowerCase()) 
 ); 
 
 // Pagination logic
 const indexOfLastItem = currentPage * itemsPerPage;
 const indexOfFirstItem = indexOfLastItem - itemsPerPage;
 const currentRoles = filteredRoles.slice(indexOfFirstItem, indexOfLastItem);
 const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
 
 const paginate = (pageNumber) => setCurrentPage(pageNumber);
 
 // Group permissions by prefix (e.g., "read", "manage", "delete") 
 const groupedPermissions = permissions.reduce((acc, perm) => { 
 const [action, module] = perm.name.includes(':') ? perm.name.split(':') : ['other', perm.name]; 
 if (!acc[module]) acc[module] = []; 
 acc[module].push(perm); 
 return acc; 
 }, {});

 // Filter grouped permissions by search
 const filteredGroupedPermissions = permSearch.trim()
 ? Object.fromEntries(
 Object.entries(groupedPermissions)
 .map(([module, perms]) => [
 module,
 perms.filter(p =>
 p.name.toLowerCase().includes(permSearch.toLowerCase()) ||
 (p.description || "").toLowerCase().includes(permSearch.toLowerCase()) ||
 module.toLowerCase().includes(permSearch.toLowerCase())
 ),
 ])
 .filter(([, perms]) => perms.length > 0)
 )
 : groupedPermissions; 
 
 const handleRoleChange = (e) => { 
 const { name, value } = e.target; 
 setNewRole((prev) => ({ ...prev, [name]: value })); 
 }; 
 
 const closeRoleModal = () => { 
 setNewRole({ name: "", description: "", permissions: [] }); 
 setCloneRoleId(""); 
 setPermSearch("");
 setRoleModal(false); 
 }; 
 
 const handleCloneRolePermissions = (roleId) => { 
 setCloneRoleId(roleId); 
 
 if (!roleId) return; 
 
 const sourceRole = roles.find(role => role._id === roleId); 
 const clonedPermissions = (sourceRole?.permissions || []) 
 .map(permission => permission._id || permission) 
 .filter(Boolean); 
 
 setNewRole(prev => ({ 
 ...prev, 
 permissions: clonedPermissions, 
 })); 
 }; 
 
 const handleEditRoleChange = (e) => { 
 const { name, value } = e.target; 
 setEditRole((prev) => ({ ...prev, [name]: value })); 
 }; 
 
 const handlePermissionToggle = (permId, isEdit = false) => { 
 if (isEdit) { 
 setEditRole(prev => { 
 const currentPerms = prev.permissions.map(p => p._id || p); 
 if (currentPerms.includes(permId)) { 
 return { ...prev, permissions: prev.permissions.filter(p => (p._id || p) !== permId) }; 
 } else { 
 return { ...prev, permissions: [...prev.permissions, permId] }; 
 } 
 }); 
 } else { 
 setNewRole(prev => { 
 if (prev.permissions.includes(permId)) { 
 return { ...prev, permissions: prev.permissions.filter(p => p !== permId) }; 
 } else { 
 return { ...prev, permissions: [...prev.permissions, permId] }; 
 } 
 }); 
 } 
 }; 
 
 const handleSelectAll = (isEdit = false) => { 
 const allPermIds = permissions.map(p => p._id); 
 if (isEdit) { 
 setEditRole(prev => ({ ...prev, permissions: allPermIds })); 
 } else { 
 setNewRole(prev => ({ ...prev, permissions: allPermIds })); 
 } 
 }; 
 
 const handleDeselectAll = (isEdit = false) => { 
 if (isEdit) { 
 setEditRole(prev => ({ ...prev, permissions: [] })); 
 } else { 
 setNewRole(prev => ({ ...prev, permissions: [] })); 
 } 
 }; 
 
 const handleRoleSubmit = async (e) => { 
 e.preventDefault(); 
 try { 
 const res = await api.post('/roles', newRole); 
 setRoles((prev) => [...prev, res.data.data]); 
 closeRoleModal(); 
 swal("Success", "Role added successfully!", "success"); 
 fetchRoles(); // Refresh to get populated permissions 
 } catch (err) { 
 swal("Error", err.response?.data?.message || "Failed to add role", "error"); 
 } 
 }; 
 
 const handleEditRoleSubmit = async (e) => { 
 e.preventDefault(); 
 try { 
 const payload = { 
 ...editRole, 
 permissions: editRole.permissions.map(p => p._id || p) 
 }; 
 const res = await api.patch(`/roles/${editRole._id}`, payload); 
 setRoles((prev) => 
 prev.map((r) => (r._id === editRole._id ? res.data.data : r)) 
 ); 
 setEditRole(null); 
 setEditRoleModal(false); 
 swal("Success", "Role updated!", "success"); 
 fetchRoles(); 
 } catch (err) { 
 swal("Error", err.response?.data?.message || "Failed to update role", "error"); 
 } 
 }; 
 
 const handleDeleteRole = async (roleId) => { 
 const willDelete = await swal({ 
 title: "Are you sure?", 
 text: "This role will be permanently deleted!", 
 icon: "warning", 
 buttons: ["Cancel", "Delete"], 
 dangerMode: true, 
 }); 
 
 if (!willDelete) return; 
 
 try { 
 await api.delete(`/roles/${roleId}`); 
 setRoles((prev) => prev.filter((r) => r._id !== roleId)); 
 swal("Deleted!", "Role deleted successfully!", "success"); 
 } catch (err) { 
 swal("Error", err.response?.data?.message || "Failed to delete role", "error"); 
 } 
 }; 
 
 const svgMenu = ( 
 <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1"> 
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"> 
 <circle fill="#000000" cx="5" cy="12" r="2"></circle> 
 <circle fill="#000000" cx="12" cy="12" r="2"></circle> 
 <circle fill="#000000" cx="19" cy="12" r="2"></circle> 
 </g> 
 </svg> 
 ); 
 
 return ( 
 <Fragment> 
 <PageTitle activeMenu="Roles" motherMenu="Users" /> 
 <Row> 
 <Col lg={12}> 
 <Card className="shadow-sm border-0"> 
 <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap"> 
 <Card.Title className="text-primary fw-bold mb-0">Roles Management</Card.Title> 
 <div className="d-flex align-items-center flex-wrap gap-2 mt-2 mt-sm-0"> 
 <div className="input-group input-group-sm" style={{ width: '250px' }}> 
 <span className="input-group-text bg-light border-end-0">
 <i className="fa fa-search text-muted"></i>
 </span>
 <input 
 type="text" 
 className="form-control form-control-sm bg-light border-start-0" 
 placeholder="Search roles..." 
 value={searchQuery} 
 onChange={(e) => {
 setSearchQuery(e.target.value);
 setCurrentPage(1);
 }} 
 /> 
 </div> 
 {canCreateRole && ( 
 <button 
 className="btn btn-primary shadow-sm px-3" 
 onClick={() => setRoleModal(true)} 
 > 
 <i className="fa fa-plus me-2"></i>Add Role 
 </button> 
 )} 
 </div> 
 </Card.Header> 
 <Card.Body className="p-0"> 
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle"> <thead className="bg-light"> 
 <tr> 
 <th className="px-4 py-3 text-uppercase fs-12 font-w600 text-muted" style={{ width: '50px' }}>#</th> 
 <th className="py-3 text-uppercase fs-12 font-w600 text-muted">Name</th> 
 <th className="py-3 text-uppercase fs-12 font-w600 text-muted">Description</th> 
 <th className="py-3 text-uppercase fs-12 font-w600 text-muted">Permissions</th> 
 {(canEditRole || canDeleteRole) && <th className="text-end px-4 py-3 text-uppercase fs-12 font-w600 text-muted">Action</th>} 
 </tr> 
 </thead> 
 <tbody> 
 {currentRoles.length > 0 ? ( 
 currentRoles.map((role, i) => ( 
 <tr key={role._id}> 
 <td className="px-4 text-muted">{indexOfFirstItem + i + 1}</td> 
 <td className="font-w600 text-dark">{role.name}</td> 
 <td className="text-muted">{role.description || "No description"}</td> 
 <td style={{ maxWidth: '400px' }}> 
 <div className="d-flex flex-wrap gap-1"> 
 {role.permissions && role.permissions.slice(0, 5).map(p => ( 
 <Badge key={p._id || p} bg="light" className="text-primary border border-primary-light"> 
 {p.name || p} 
 </Badge> 
 ))}
 {role.permissions?.length > 5 && (
 <Badge bg="light" className="text-muted border">
 +{role.permissions.length - 5} more
 </Badge>
 )} 
 </div> 
 </td> 
 {(canEditRole || canDeleteRole) && ( 
 <td className="text-end px-4"> 
 <Dropdown className="ms-auto"> 
 <Dropdown.Toggle 
 as="div" 
 className="btn-link p-0 no-caret" 
 style={{ cursor: "pointer" }} 
 > 
 {svgMenu} 
 </Dropdown.Toggle> 
 
 <Dropdown.Menu align="end" className="border-0 shadow-sm" as={PortalMenu}> 
 {canEditRole && (
 <Dropdown.Item 
 className="py-2"
 onClick={() => { 
 setEditRole(role); 
 setEditRoleModal(true); 
 }} 
 > 
 <i className="fa fa-edit me-2 text-primary"></i> Edit 
 </Dropdown.Item> 
 )} 
 
 {canDeleteRole && role.name.toLowerCase() !== 'admin' && ( 
 <Dropdown.Item 
 className="text-danger py-2" 
 onClick={() => handleDeleteRole(role._id)} 
 > 
 <i className="fa fa-trash me-2"></i> Delete 
 </Dropdown.Item> 
 )} 
 </Dropdown.Menu> 
 </Dropdown> 
 </td> 
 )} 
 </tr> 
 )) 
 ) : ( 
 <tr> 
 <td colSpan={(canEditRole || canDeleteRole) ? 5 : 4} className="text-center py-4"> 
 No roles found matching your search. 
 </td> 
 </tr> 
 )} 
 </tbody> 
 </Table> 
 </div>
 <AppPagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={filteredRoles.length}
 itemsLength={currentRoles.length}
 limit={itemsPerPage}
 onPageChange={paginate}
 />
 </Card.Body> 
 </Card> 
 </Col> 
 </Row> 
 
 {/* ADD ROLE MODAL */} 
 <Modal show={roleModal} onHide={closeRoleModal} size="lg" centered className="modal-light"> 
 <Modal.Header closeButton className="border-bottom-0"> 
 <Modal.Title className="text-primary fw-bold">Add New Role</Modal.Title> 
 </Modal.Header> 
 <Form onSubmit={handleRoleSubmit}> 
 <Modal.Body className="py-4"> 
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Role Name</Form.Label> 
 <Form.Control 
 type="text" 
 name="name" 
 value={newRole.name} 
 onChange={handleRoleChange} 
 placeholder="e.g. Project Manager" 
 required 
 className="bg-light border-0 py-2"
 /> 
 </Form.Group> 
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Description</Form.Label> 
 <Form.Control 
 as="textarea" 
 name="description" 
 rows={3}
 value={newRole.description} 
 onChange={handleRoleChange} 
 placeholder="What are the responsibilities of this role?" 
 className="bg-light border-0"
 /> 
 </Form.Group> 
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Clone Permissions From Role</Form.Label> 
 <Form.Select 
 value={cloneRoleId} 
 onChange={(e) => handleCloneRolePermissions(e.target.value)} 
 className="bg-light border-0 py-2"
 > 
 <option value="">Select role to copy permissions</option> 
 {roles.map(role => ( 
 <option key={role._id} value={role._id}> 
 {role.name} 
 </option> 
 ))} 
 </Form.Select> 
 <div className="text-muted fs-12 mt-2"> 
 After cloning, you can still add or remove permissions below. 
 </div> 
 </Form.Group> 
 <Form.Group className="mb-0"> 
 <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3"> 
 <Form.Label className="mb-0 text-dark fw-bold">Select Module Permissions</Form.Label> 
 <div className="d-flex gap-2 align-items-center"> 
 <div className="input-group input-group-sm" style={{ width: 200 }}>
 <span className="input-group-text bg-light border-0">
 <i className="fa fa-search text-muted" style={{ fontSize: 12 }}></i>
 </span>
 <input
 type="text"
 className="form-control bg-light border-0"
 placeholder="Search permissions..."
 value={permSearch}
 onChange={(e) => setPermSearch(e.target.value)}
 />
 {permSearch && (
 <button
 type="button"
 className="btn btn-light border-0"
 onClick={() => setPermSearch("")}
 >
 <i className="fa fa-times text-muted" style={{ fontSize: 11 }}></i>
 </button>
 )}
 </div>
 <button type="button" className="btn btn-xs btn-outline-primary px-3 rounded-pill" onClick={() => handleSelectAll(false)}>Select All</button> 
 <button type="button" className="btn btn-xs btn-outline-secondary px-3 rounded-pill" onClick={() => handleDeselectAll(false)}>Clear</button> 
 </div> 
 </div> 
 <div className="permission-grid-container pe-2">
 <Row className="gx-3 gy-3">
 {Object.entries(filteredGroupedPermissions).map(([module, perms]) => ( 
 <Col key={module} lg={6} xl={4}> 
 <div className="perm-card p-3 rounded-3 shadow-sm h-100">
 <div className="perm-module-title text-primary fw-bold text-uppercase d-flex align-items-center mb-3">
 <i className="fa fa-folder-open me-2 opacity-50"></i>
 {module}
 </div>
 <div className="perm-list d-flex flex-column gap-2">
 {perms.map(perm => ( 
 <Form.Check 
 key={perm._id}
 type="checkbox" 
 id={`new-perm-${perm._id}`} 
 label={<span className="fs-13 text-dark">{perm.name}</span>} 
 checked={newRole.permissions.includes(perm._id)} 
 onChange={() => handlePermissionToggle(perm._id)} 
 className="custom-checkbox m-0"
 /> 
 ))} 
 </div>
 </div>
 </Col> 
 ))} 
 </Row> 
 </div>
 </Form.Group> 
 </Modal.Body> 
 <Modal.Footer className="border-top-0 pt-0"> 
 <button className="btn btn-light shadow-none text-muted" type="button" onClick={closeRoleModal}>Cancel</button> 
 <button className="btn btn-primary px-4 shadow-sm" type="submit">Create Role</button> 
 </Modal.Footer> 
 </Form> 
 </Modal> 
 
 {/* EDIT ROLE MODAL */} 
 <Modal show={editRoleModal} onHide={() => { setEditRoleModal(false); setPermSearch(""); }} size="lg" centered className="modal-light"> 
 <Modal.Header closeButton className="border-bottom-0"> 
 <Modal.Title className="text-primary fw-bold">Edit Role Settings</Modal.Title> 
 </Modal.Header> 
 <Form onSubmit={handleEditRoleSubmit}> 
 <Modal.Body className="py-4"> 
 {editRole && ( 
 <Fragment> 
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Role Name</Form.Label> 
 <Form.Control 
 type="text" 
 name="name" 
 value={editRole.name} 
 onChange={handleEditRoleChange} 
 required 
 disabled={editRole.name.toLowerCase() === 'admin'} 
 className="bg-light border-0 py-2"
 /> 
 </Form.Group> 
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Description</Form.Label> 
 <Form.Control 
 as="textarea" 
 name="description" 
 rows={3}
 value={editRole.description} 
 onChange={handleEditRoleChange} 
 className="bg-light border-0"
 /> 
 </Form.Group> 
 <Form.Group className="mb-0"> 
 <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3"> 
 <Form.Label className="mb-0 text-dark fw-bold">Modify Permissions</Form.Label> 
 <div className="d-flex gap-2 align-items-center"> 
 <div className="input-group input-group-sm" style={{ width: 200 }}>
 <span className="input-group-text bg-light border-0">
 <i className="fa fa-search text-muted" style={{ fontSize: 12 }}></i>
 </span>
 <input
 type="text"
 className="form-control bg-light border-0"
 placeholder="Search permissions..."
 value={permSearch}
 onChange={(e) => setPermSearch(e.target.value)}
 />
 {permSearch && (
 <button
 type="button"
 className="btn btn-light border-0"
 onClick={() => setPermSearch("")}
 >
 <i className="fa fa-times text-muted" style={{ fontSize: 11 }}></i>
 </button>
 )}
 </div>
 <button type="button" className="btn btn-xs btn-outline-primary px-3 rounded-pill" onClick={() => handleSelectAll(true)}>Select All</button> 
 <button type="button" className="btn btn-xs btn-outline-secondary px-3 rounded-pill" onClick={() => handleDeselectAll(true)}>Clear</button> 
 </div> 
 </div> 
 <div className="permission-grid-container pe-2">
 <Row className="gx-3 gy-3">
 {Object.entries(filteredGroupedPermissions).map(([module, perms]) => ( 
 <Col key={module} lg={6} xl={4}> 
 <div className="perm-card p-3 rounded-3 shadow-sm h-100">
 <div className="perm-module-title text-primary fw-bold text-uppercase d-flex align-items-center mb-3">
 <i className="fa fa-folder-open me-2 opacity-50"></i>
 {module}
 </div>
 <div className="perm-list d-flex flex-column gap-2">
 {perms.map(perm => ( 
 <Form.Check 
 key={perm._id}
 type="checkbox" 
 id={`edit-perm-${perm._id}`} 
 label={<span className="fs-13 text-dark">{perm.name}</span>} 
 checked={editRole.permissions.some(p => (p._id || p) === perm._id)} 
 onChange={() => handlePermissionToggle(perm._id, true)} 
 className="custom-checkbox m-0"
 /> 
 ))} 
 </div>
 </div>
 </Col> 
 ))} 
 </Row> 
 </div>
 </Form.Group> 
 </Fragment> 
 )} 
 </Modal.Body> 
 <Modal.Footer className="border-top-0 pt-0"> 
 <button className="btn btn-light shadow-none text-muted" type="button" onClick={() => setEditRoleModal(false)}>Cancel</button> 
 <button className="btn btn-primary px-4 shadow-sm" type="submit">Update Role</button> 
 </Modal.Footer> 
 </Form> 
 </Modal> 
 <style>{`
  .bg-light-soft { background-color: #fbfbfe; }
  .fs-12 { font-size: 11.5px; }
  .fs-13 { font-size: 13px; }
  .fs-14 { font-size: 14px; }
  .custom-checkbox .form-check-input { border-color: #e0e0e0; }
  .custom-checkbox .form-check-input:checked { background-color: #886cff; border-color: #886cff; }
  .border-primary-light { border-color: rgba(136, 108, 255, 0.1) !important; }
  .font-w600 { font-weight: 600; }
  .text-dark { color: #2d3436 !important; }
  .text-muted { color: #7f8c8d !important; }
  .modal-content { border: none; border-radius: 12px; }
  .modal-header { background-color: #fff; border-bottom: 1px solid #f1f2f6; }
  .bg-light { background-color: #f8faff !important; }
  .perm-card { 
    transition: all 0.2s ease; 
    border: 1px solid #f1f2f6;
    background: #fff;
  }
  .perm-card:hover { 
    border-color: #886cff;
    box-shadow: 0 4px 12px rgba(136, 108, 255, 0.05);
  }
  .perm-module-title {
    background: #f8faff;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 12px;
    font-size: 12px;
    letter-spacing: 0.5px;
  }
  .permission-grid-container {
    max-height: 500px;
    overflow-y: auto;
    padding: 10px 5px;
  }
  .modal-lg { max-width: 1000px; }
  .modal-body { max-height: 85vh; overflow-y: auto; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f1f1f1; }
  ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
  .custom-pagination .page-link { color: #886cff; border-radius: 4px; margin: 0 2px; border: none; background: #f8faff; }
  .custom-pagination .page-item.active .page-link { background-color: #886cff; color: #fff; }
  .custom-pagination .page-item.disabled .page-link { background: transparent; color: #ccc; }

  /* Dark mode styling */
  [data-theme-version="dark"] .modal-header { 
    background-color: #1e1e2d !important; 
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important; 
  }
  [data-theme-version="dark"] .modal-content { 
    background-color: #1e1e2d !important; 
    color: #f1f5f9 !important; 
  }
  [data-theme-version="dark"] .modal-title { 
    color: #ffffff !important; 
  }
  [data-theme-version="dark"] .modal-header .btn-close { 
    filter: invert(1) grayscale(100%) brightness(200%) !important; 
  }
  [data-theme-version="dark"] .text-dark { 
    color: #f1f5f9 !important; 
  }
  [data-theme-version="dark"] .text-muted { 
    color: #94a3b8 !important; 
  }
  [data-theme-version="dark"] .modal-body .border-bottom {
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] .modal-body .form-control,
  [data-theme-version="dark"] .modal-body .form-select {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-body .form-control::placeholder {
    color: #64748b !important;
  }
  [data-theme-version="dark"] .modal-body .form-select option {
    background-color: #1e1e2d !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-body .input-group-text {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    border-right: none !important;
    color: #94a3b8 !important;
  }
  [data-theme-version="dark"] .modal-body .input-group .form-control {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    border-left: none !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-body .input-group .btn-light {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    border-left: none !important;
    color: #94a3b8 !important;
  }
  [data-theme-version="dark"] .perm-card {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  }
  [data-theme-version="dark"] .perm-card:hover {
    border-color: #886cff !important;
    box-shadow: 0 4px 14px rgba(136, 108, 255, 0.2) !important;
  }
  [data-theme-version="dark"] .perm-module-title {
    background-color: rgba(136, 108, 255, 0.15) !important;
    color: #a78bfa !important;
    border: 1px solid rgba(136, 108, 255, 0.25) !important;
  }
  [data-theme-version="dark"] .perm-card .text-dark,
  [data-theme-version="dark"] .perm-card .form-check-label span {
    color: #e2e8f0 !important;
  }
  [data-theme-version="dark"] .custom-checkbox .form-check-input {
    background-color: #242526 !important;
    border-color: #4b5563 !important;
  }
  [data-theme-version="dark"] .custom-checkbox .form-check-input:checked {
    background-color: #886cff !important;
    border-color: #886cff !important;
  }
  [data-theme-version="dark"] .btn-outline-primary {
    color: #886cff !important;
    border-color: #886cff !important;
  }
  [data-theme-version="dark"] .btn-outline-primary:hover {
    background-color: #886cff !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .btn-outline-secondary {
    color: #f87171 !important;
    border-color: #ef4444 !important;
  }
  [data-theme-version="dark"] .btn-outline-secondary:hover {
    background-color: #ef4444 !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-footer .btn-light {
    background-color: rgba(255, 255, 255, 0.08) !important;
    color: #cbd5e1 !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
  }
  [data-theme-version="dark"] .modal-footer .btn-light:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .card-header.bg-white {
    background-color: #1e1e2d !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] .input-group-text.bg-light {
    background-color: #161717 !important;
    border-color: #2b2b2b !important;
    color: #94a3b8 !important;
  }
  [data-theme-version="dark"] .form-control.bg-light {
    background-color: #161717 !important;
    border-color: #2b2b2b !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] thead.bg-light th {
    background-color: #161717 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] .badge.bg-light {
    background-color: rgba(136, 108, 255, 0.15) !important;
    color: #a78bfa !important;
    border-color: rgba(136, 108, 255, 0.25) !important;
  }
  [data-theme-version="dark"] ::-webkit-scrollbar-track {
    background: #1e1e2d !important;
  }
  [data-theme-version="dark"] ::-webkit-scrollbar-thumb {
    background: #333b4d !important;
  }
  `}</style>
 </Fragment> 
 ); 
 }; 
 
 export default Roles; 
